import { DateTime } from 'luxon';
import { bootstrappedData } from 'utils/bootstrap';
import {
  type AgentSessionsQuery,
  type AgentSessionsResponse,
  getAgentSessions,
} from './agentSessions.api';
import { createAgentSessionsCache } from './agentSessions.cache';

const cache = createAgentSessionsCache();
const scope = (progressive = false) =>
  `${bootstrappedData.account?.id}:${bootstrappedData.user?.id}:${progressive}`;
export const peekAgentSessions = (query: AgentSessionsQuery, progressive = false) =>
  cache.peek(scope(progressive), query);
export const loadAgentSessions = (query: AgentSessionsQuery) =>
  cache.load(scope(), query, () => getAgentSessions(query));
export const invalidateAgentSessions = () => cache.invalidate();

export async function fetchAgentSessionPages(
  query: AgentSessionsQuery,
  publish: (response: AgentSessionsResponse) => void,
  fetch = getAgentSessions
): Promise<AgentSessionsResponse> {
  let response = await fetch({ ...query, pageSize: 15 });
  const sessions = new Map(response.sessions.map(session => [session.id, session]));
  const cursors = new Set<string>();
  while (response.next_cursor) {
    if (cursors.has(response.next_cursor))
      throw new Error('Session pagination did not advance. Refresh to try again.');
    cursors.add(response.next_cursor);
    publish({ ...response, sessions: [...sessions.values()] });
    // Let React commit the first rows before preparing another batch.
    await new Promise(resolve => setTimeout(resolve, 0));
    response = await fetch({ ...query, pageSize: 50, cursor: response.next_cursor });
    for (const session of response.sessions) sessions.set(session.id, session);
  }
  return { ...response, sessions: [...sessions.values()] };
}

export const loadAgentSessionsProgressively = (
  query: AgentSessionsQuery,
  publish?: (response: AgentSessionsResponse) => void
) => cache.load(scope(true), query, onPage => fetchAgentSessionPages(query, onPage), publish);

export function prefetchAgentSessionsForRoute(enabled: boolean, pathname: string, search: string) {
  if (!enabled || pathname !== `/${bootstrappedData.account?.id}/agent-tracking`) return;
  const parsed = DateTime.fromISO(new URLSearchParams(search).get('date') ?? '');
  const start = (parsed.isValid ? parsed : DateTime.local()).startOf('week');
  // Speculation must not block app boot; the page owns error reporting/retry.
  void loadAgentSessionsProgressively({
    from: start.toISODate()!,
    to: start.plus({ days: 6 }).toISODate()!,
  }).catch(() => {});
}
